import { formatDate } from '@pb/utils'

import { Packages, StatusCode, ButtonDetails } from '../duck/types'

const getStoreStreetName = (storeInfo): any => {
  return storeInfo?.address?.streetName1
}

export type OrderTimelineEvent = {
  title: string
  description: string
  timestamp: string | null
  isCompleted: boolean
  isActive: boolean
  isCurrentSelected: boolean
  statusCode?: StatusCode
  selectedStatus?: string
  subStatus?: string | null
  buttonDetails?: ButtonDetails[] | null
  subStatusType?: string | null
  subStatusIndicator?: 'red' | 'green' | string | null
  isDigitalReturnsEnabled?: boolean
}

type ButtonDetailsType = {
  name: string
  href: string | null
  isExteralLink?: boolean
  label?: string
  isSecondary: boolean
}
export type OrderStatusEvent = {
  title: string
  description?: string
  timestamp?: string | null
  isCompleted?: boolean
  isActive?: boolean
  isCurrentSelected?: boolean
  statusCode?: StatusCode
  selectedStatus?: string
  subStatus?: string | null
  buttonDetails?: ButtonDetails[] | null
  subStatusType?: string | null
  subStatusIndicator?: 'red' | 'green' | string | null
  current?: boolean
  past?: boolean
  success?: boolean
  error?: boolean
  buttonProps?: ButtonDetailsType[]
  isDigitalReturnsEnabled?: boolean
}

const buttonDetailsProps: ButtonDetailsType[] = [
  {
    name: 'trackOrder',
    isExteralLink: true,
    href: null,
    isSecondary: true,
  },
  {
    name: 'returnOrder',
    isExteralLink: true,
    href: null,
    isSecondary: true,
  },
  {
    name: 'rateAndReview',
    isExteralLink: true,
    href: null,
    isSecondary: false,
  },
]

const buttonPropsRendering = (btns, isDigitalReturnsEnabled, isCodFlow): ButtonDetailsType[] => {
  const filteredProps: any[] = []

  if (btns?.length > 0) {
    filteredProps.push(
      btns.map((item) => {
        const index = buttonDetailsProps.findIndex((prop) => prop.name === item.actionType)

        buttonDetailsProps[index].label = item.label

        if (buttonDetailsProps[index].name === 'returnOrder' && !isCodFlow) {
          buttonDetailsProps[index].isExteralLink = !(
            process.env.IS_DIGITAL_RETURNS_ENABLED === 'enabled' && isDigitalReturnsEnabled
          )
        }

        return buttonDetailsProps[index]
      }),
    )

    return filteredProps[0]
  }

  return filteredProps
}

const getOrderTimelineEvents = (
  statusBar: Packages['statusBar'],
  trackAndReturn?,
  rateAndReviewHref?,
  isDigitalReturnsEnabled?,
): OrderTimelineEvent[] => {
  buttonDetailsProps[0].href = trackAndReturn.trackingUrl
  buttonDetailsProps[1].href = trackAndReturn.codReturnUrl
    ? trackAndReturn.codReturnUrl
    : trackAndReturn.returnUrl
  buttonDetailsProps[2].href = rateAndReviewHref
  buttonDetailsProps[1].isExteralLink = !trackAndReturn.codReturnUrl
  // If previous and current event is selected but not the next, the current is the active one.
  // If the last event is selected, all events should be completed and no one should be active.
  return statusBar.map((event, index) => {
    const nextIndex = index + 1
    const isCurrentSelected = event.selected
    const isNextSelected = nextIndex !== statusBar.length && statusBar[nextIndex].selected
    const isFinalEventCompleted = index === statusBar.length - 1 && isCurrentSelected
    return {
      title: event.statusName,
      description: event.statusDescription,
      timestamp: event.timestamp && formatDate(event.timestamp),
      isActive: isCurrentSelected && !isNextSelected && !isFinalEventCompleted,
      isCompleted: isNextSelected || isFinalEventCompleted,
      isCurrentSelected,
      statusCode: event.statusCode?.toLowerCase(),
      selectedStatus: event.selectedStatus?.toLowerCase(),
      subStatus: event.subStatus,
      buttonDetails: event.buttonDetails,
      subStatusType: event.subStatusType,
      subStatusIndicator: event.subStatusIndicator?.toLowerCase(),
      current: event.selectedStatus?.toLocaleLowerCase() === 'current',
      past: event.selectedStatus?.toLocaleLowerCase() === 'past',
      success: event.subStatusIndicator?.toLocaleLowerCase() === 'green',
      error: event.subStatusIndicator?.toLocaleLowerCase() === 'red',
      buttonProps:
        event.selectedStatus?.toLowerCase() === 'current'
          ? buttonPropsRendering(
              event.buttonDetails,
              isDigitalReturnsEnabled,
              !!trackAndReturn.codReturnUrl,
            )
          : [],
    }
  })
}

const getPastMonths = (minDate: string, maxDate: string): number => {
  const min = new Date(minDate)
  const max = new Date(maxDate)
  const yearsDiff = max.getFullYear() - min.getFullYear()

  const months = yearsDiff * 12 + (max.getMonth() - min.getMonth())

  if (months <= 0) {
    return 1
  }

  return months
}

// @ts-expect-error The right-hand side of an arithmetic operation must be of return [...getOnlineOrders(state), ...getStoreOrders(state type 'any', 'number', 'bigint' or an enum type.
const sortByDate = (orderA, orderB): any => new Date(orderB.date) - new Date(orderA.date)

export { getStoreStreetName, getOrderTimelineEvents, getPastMonths, sortByDate }
